//
// ChatQuestionnaireInteraction
//
// A committed questionnaire interaction event: the question quoted as a thin
// outlined pill sitting behind the user's answer, which renders as an ordinary
// message bubble (reusing MessageFrame). Laid out with the same Row + ordering as a
// normal message so the avatar/date land exactly where they do on any other
// authored message; the pill is offset outward and the answer overlaps it in front.
// Resolved dumbly from the frozen qstnId/items snapshot — the graph is never parsed
// nor its revision consulted. Display attribution is decoupled from the DB author:
// the question reads as the questionnaire's even though the event was authored by
// the answering user.
//
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { memo } from 'react';

import {
  Column,
  Row,
} from '../../../display';

import ChatMessageControls from './ChatMessageControls';
import {
  ChatAuthorIcon,
  ChatAuthorName,
  ChatDate,
  ChatItemClick,
  ChatSpacer,
  MessageFrame,
} from './components';
import ContextPill from './ContextPill';
import questionnaireNodeText from './questionnaireNodeText';
import renderQuestionnaireAnswerItems from './renderQuestionnaireAnswerItems';

const ChatQuestionnaireInteraction = memo(({
  registerChild,
  translate,
  style,
  message,
  author,
  initials,
  crBlocked,
  color,
  gcatCode,
  grpGpid,
  grpLbl,
  grpNbr,
  visibleInThisGathering = true,
  groupsMap,
  imagesMap,
  prtnrLogoUrl,
  selectGathering,
}) => {
  const {
    id,
    msgRdatim1,
    qstnId,
    items,
    answers,
    gthrId,
  } = message;

  const questionText = questionnaireNodeText(items, qstnId, undefined, translate);
  const answerNodes = renderQuestionnaireAnswerItems(items, answers, translate, author);

  if ((!questionText) && (!answerNodes.length)) return null;

  // The interaction sits on its author's side: the answering user (mine, right)
  //  unless rendered for a viewer who is not that user (author set, left).
  const mine = !author;

  return (
    <div
      ref={registerChild}
      key={id}
      className={classNames(
        'w-full py-2 gap-1 flex flex-col relative items-center justify-center overflow-hidden group',
        {
          'px-6': visibleInThisGathering,
          'px-2 opacity-50': !visibleInThisGathering,
        },
      )}
      style={style}
    >
      <ChatItemClick
        gthrId={gthrId}
        selectGathering={selectGathering}
        visibleInThisGathering={visibleInThisGathering}
      />

      <ChatAuthorName
        translate={translate}
        author={author}
        initials={initials}
        crBlocked={crBlocked}
        gcatCode={gcatCode}
        grpNbr={grpNbr}
        prtnrLogoUrl={prtnrLogoUrl}
      />

      <Row
        start={!!author}
        end={!author}
        fullw
      >
        <ChatAuthorIcon
          author={author}
          initials={initials}
          color={color}
          gcatCode={gcatCode}
          grpGpid={grpGpid}
          grpLbl={grpLbl}
          groupsMap={groupsMap}
          imagesMap={imagesMap}
        />

        <div className={classNames(
          'order-3 flex flex-col min-w-0 max-w-[85%]',
          (mine) ? 'items-end' : 'items-start',
        )}
        >
          <ContextPill
            author={author}
            text={questionText}
          />

          {(answerNodes.length)
            ? (
                <div className={classNames(
                  'relative -mt-1',
                  (mine) ? 'self-end' : 'self-start',
                )}
                >
                  <MessageFrame
                    author={author}
                    noSpacer
                    className="ring-2 ring-pxMsgsBgnd"
                  >
                    {answerNodes}
                  </MessageFrame>
                </div>
              )
            : null}
        </div>

        <Column
          className={classNames(
            'self-start',
            {
              'order-2': !author,
              'order-4': author,
            },
          )}
          gap={1}
          crossStart={author}
          crossEnd={!author}
        >
          <ChatDate
            dtime={msgRdatim1}
            myMessage={!author}
          />

          <ChatMessageControls
            translate={translate}
            message={message}
            author={author}
          />
        </Column>

        <ChatSpacer
          author={author}
        />
      </Row>
    </div>
  );
});
ChatQuestionnaireInteraction.displayName = 'ChatQuestionnaireInteraction';

ChatQuestionnaireInteraction.propTypes = {
  registerChild: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
  style: PropTypes.shape({}).isRequired,
  author: PropTypes.string,
  initials: PropTypes.string,
  crBlocked: PropTypes.number,
  color: PropTypes.string,
  gcatCode: PropTypes.string,
  grpGpid: PropTypes.string,
  grpLbl: PropTypes.string,
  grpNbr: PropTypes.number,
  visibleInThisGathering: PropTypes.bool,
  groupsMap: PropTypes.instanceOf(Map).isRequired,
  imagesMap: PropTypes.instanceOf(Map).isRequired,
  prtnrLogoUrl: PropTypes.string,
  selectGathering: PropTypes.func,
  message: PropTypes.shape({
    id: PropTypes.number.isRequired,
    msgRdatim1: PropTypes.string.isRequired,
    qstnId: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({})),
    answers: PropTypes.shape({}),
    gthrId: PropTypes.number,
  }).isRequired,
};

export default ChatQuestionnaireInteraction;
